// src/components/LegalConsent.jsx
//
// WP-3 reusable clickwrap + age/parental-consent gate. NOT in the prototype:
// built by EXTENDING the prototype design language (CLAUDE.md section 1.7) -- same
// font-display labels, accent borders, theme tokens. Used at signup, checkout, and
// manual PayMe/FPS submit.
//
// Two required confirmations (locked #15 PDPO / under-18):
//   1. Age / parental consent.
//   2. Agreement to Terms + Privacy (+ Refund where relevant), linking to /legal.
//
// Reports consent state up via onConsentChange({ consented, versions, types }).
// `versions` is the current {type: version} map (empty in mock / if unseeded);
// the parent records the formal consent via recordLegalConsent and passes
// acceptedLegalVersions to the checkout CF.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLegalDocs, LEGAL_TYPE_LABELS } from '../storage/useLegalDocs.js';
import { legalConsentReady } from './legalConsentGate.js';
import { AUTH_MODE } from '../auth/firebase.js';

const THEMES = {
    charcoal: { accent: 'text-charcoal-accent', text: 'text-charcoal-text', muted: 'text-charcoal-muted', border: 'border-charcoal-accent/30', box: 'bg-charcoal-pageDark/50' },
    burgundy: { accent: 'text-burgundy-accent', text: 'text-burgundy-text', muted: 'text-burgundy-muted', border: 'border-burgundy-accent/30', box: 'bg-burgundy-pageDark/40' },
    midnight: { accent: 'text-midnight-accent', text: 'text-midnight-text', muted: 'text-midnight-muted', border: 'border-midnight-accent/30', box: 'bg-midnight-pageDark/40' },
    gold: { accent: 'text-book-accent', text: 'text-book-text', muted: 'text-book-muted', border: 'border-book-accent/30', box: 'bg-book-page/40' }
};

export default function LegalConsent({
    theme = 'charcoal',
    requireTypes = ['terms_of_service', 'privacy_policy', 'refund_policy'],
    onConsentChange
}) {
    const t = THEMES[theme] || THEMES.charcoal;
    const { versions, loading, error, reload } = useLegalDocs();
    const [ageConfirmed, setAgeConfirmed] = useState(false);
    const [legalAgreed, setLegalAgreed] = useState(false);

    // In mock mode there is no backend to record against, but the UI flow still
    // gates on both boxes. In real/emulator mode we also need the docs present.
    // The gate itself lives in legalConsentGate.js (pure, invariant-tested):
    // a failed/empty legalDocuments read keeps consented FALSE -- signup and
    // payment stay blocked; the degraded block below only adds message + retry.
    const versionsReady = legalConsentReady(versions, requireTypes, AUTH_MODE === 'mock');
    const consented = ageConfirmed && legalAgreed && versionsReady;

    useEffect(() => {
        if (onConsentChange) {
            const picked = {};
            requireTypes.forEach((ty) => { if (versions[ty]) picked[ty] = versions[ty]; });
            onConsentChange({ consented, versions: picked, types: requireTypes });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consented]);

    const labels = requireTypes.map((ty) => LEGAL_TYPE_LABELS[ty] || ty);

    return (
        <div className={`border ${t.border} ${t.box} p-5 space-y-3`}>
            <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={ageConfirmed}
                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                    className="mt-1 flex-shrink-0" />
                <span className={`font-serif ${t.text} text-sm leading-relaxed`}>
                    I confirm that I am 18 or older, <span className={t.muted}>or</span> that my parent or
                    guardian consents to my use of Between the Lines and agrees to these terms on my behalf.
                </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={legalAgreed}
                    onChange={(e) => setLegalAgreed(e.target.checked)}
                    className="mt-1 flex-shrink-0" />
                <span className={`font-serif ${t.text} text-sm leading-relaxed`}>
                    I have read and agree to the{' '}
                    <Link to="/legal" target="_blank" className={`${t.accent} underline hover:opacity-80`}>
                        {labels.join(', ').replace(/, ([^,]*)$/, ' and $1')}
                    </Link>.
                </span>
            </label>

            {/* Graceful degrade (P1): a failed/empty legal-docs load used to leave the
                submit button silently disabled with no way out. Now: a calm explanation
                + a Try-again that re-runs the load. The consent gate is UNCHANGED --
                until the docs load and both boxes are ticked, the parent's button
                (signup / pay) stays disabled. */}
            {loading && (
                <p className={`font-serif italic ${t.muted} text-xs`} role="status">
                    Checking legal documents&hellip;
                </p>
            )}
            {!loading && !versionsReady && (
                <div className={`border ${t.border} p-3 space-y-2`} role="alert">
                    <p className={`font-serif italic ${t.muted} text-xs leading-relaxed`}>
                        {error
                            ? 'We could not load the legal documents needed to continue. This is usually temporary -- please try again.'
                            : 'The legal documents are not available right now. Please try again shortly, or contact support if this keeps happening.'}
                    </p>
                    <p className={`font-serif italic ${t.muted} text-xs leading-relaxed`}>
                        You will be able to continue once they load and you have agreed to them.
                    </p>
                    <button type="button" onClick={reload}
                        className={`px-3 py-1.5 border ${t.border} ${t.accent} font-display text-xs hover:opacity-80 transition-opacity font-semibold`}>
                        Try again
                    </button>
                </div>
            )}
        </div>
    );
}
